import svgArrow from '../../../assets/svg/arrow-right.svg';

// add an slight delay to requestAnimationFrame
const requestAnimationFrame = function(fn) {
	return window.requestAnimationFrame(() => window.requestAnimationFrame(fn));
}

// note: ie11 or jquery do not support classList/addClass on svg elements - manipulate class attribute directly
const addSvgClass = function( node, className ) {
	let nodeClass   = node.getAttribute('class').trim();
	let nodeClasses = nodeClass.split(' ');

	if ( nodeClasses.indexOf( className ) === -1 ) {
		nodeClasses.push( className );
		nodeClass = nodeClasses.join(' ');
		node.setAttribute( 'class', nodeClass );
	}

	return nodeClass;
}

const removeSvgClass = function( node, className ) {
	let nodeClasses = node.getAttribute('class').trim().split(' ').filter( cn => cn !== className );
	const nodeClass = nodeClasses.join(' ');

	node.setAttribute( 'class', nodeClass );

	return nodeClass;
}

class MapState {
	constructor( map, node, { cta, ctaUrl, ...data } = {} ) {
		this.map    = map;
		this.node   = node;
		this.data   = data;
		this.cta    = cta;
		this.ctaUrl = ctaUrl;

		this.detail            = document.createElement( 'div' );
		this.detail.className  = 'state-map__detail';
		this.detailActiveClass = 'state-map__detail--active';
		this.activeClass       = 'state-map__state--active';
		this.active            = false;

		this.resetCoords();

		this.key    = this.node.getAttribute('abbr');
		this.status = this.node.getAttribute('status');

		this.node.addEventListener( 'click', (e) => {
			if ( this.active ) {
				this.map.deactivate( this.key );
			} else {
				// set custom coords originating from mouse click and not center of the state
				this.coords = { x: e.clientX, y: e.clientY };

				// on desktop be relative to the map and not fixed
				if ( this.map.getMode() === 'desktop' ) {
					let mapRect = this.map.node.getBoundingClientRect();
					this.coords.x -= mapRect.left;
					this.coords.y -= mapRect.top;
				}

				this.map.activate( this.key );
			}
		} );
	}

	resetCoords() {
		const rect = this.node.getBoundingClientRect();

		this.coords = {
			x: rect.left + ( rect.width / 2 ),
			y: rect.top + ( rect.height / 2 ),
		}
	}

	activate() {
		this.template();

		addSvgClass( this.node, this.activeClass );

		this.detail.style.left = `${this.coords.x}px`;
		this.detail.style.top = `${this.coords.y}px`;
		this.map.node.appendChild( this.detail );

		requestAnimationFrame(() => {
			this.detail.classList.add( this.detailActiveClass );
			this.detail.style.top = '';
			this.detail.style.left = '';
		});

		this.active = true;
	}

	deactivate() {
		this.detail.style.left = `${this.coords.x}px`;
		this.detail.style.top = `${this.coords.y}px`;
		removeSvgClass( this.node, this.activeClass );

		requestAnimationFrame(() => {
			this.detail.classList.remove( this.detailActiveClass );

			let transitionend = (e) => {
				if ( e.target === this.detail && e.propertyName === 'transform' ) {
					this.detail.removeEventListener('transitionend', transitionend);
					this.detail.parentNode.removeChild(this.detail);
				}
			}

			this.detail.addEventListener('transitionend', transitionend);
		});

		this.active = false;
	}

	template() {
		if ( ! this.detail.innerHTML ) {
			const closeDiv = document.createElement('div');
			const close = document.createElement('button');
			close.className = 'state-map__close';
			close.addEventListener('click', () => this.map.deactivate() );
			close.innerHTML = '&times;';
			closeDiv.appendChild(close);

			this.detail.appendChild(closeDiv);

			const contentDiv = document.createElement('div');
			contentDiv.className = 'state-map__detail-content';
			this.detail.appendChild(contentDiv);

			if ( this.node.getAttribute('name') ) {
				contentDiv.insertAdjacentHTML( 'beforeend', `<h3>${this.node.getAttribute('name')}</h3>` );
			}

			Object.keys( this.data ).forEach( key => {
				contentDiv.insertAdjacentHTML( 'beforeend', `<p><strong>${key}</strong>: ${this.data[key]}`);
			} );

			if ( this.cta && this.ctaUrl ) {
				contentDiv.insertAdjacentHTML( 'beforeend', `<div class="wp-block-button"><a class="wp-block-button__link" href="${this.ctaUrl}"><span>${this.cta} ${svgArrow}</span></a></div>` );
			}
		}
	}
}

export class StatesMap {
	constructor( node, opts = {}, data = {} ) {
		this.node = node;
		this.opts = {
			activeClass: 'state-map--active',
			selectLabel: 'Select a State',
			mobileBreakpoint: 800,
			...opts,
		};
		this.data = data;

		this.active = null;
		this.map = this.node.querySelector('svg');
		this.states = {};

		this.node.insertAdjacentHTML(
			'beforeend',
			`<label>
				<span class="a11y">${this.opts.selectLabel}</span>
				<select class="state-map__select">
					<option value="">${this.opts.selectLabel}</option>
				</select>
			</label>`
		);

		this.select = this.node.querySelector('.state-map__select');
		this.select.addEventListener( 'change', () => {
			if ( this.select.value ) {
				this.states[ this.select.value ].resetCoords();
				this.activate( this.select.value );
			} else {
				this.deactivate();
			}
		} );

		Array.from( this.node.querySelectorAll( '.state-map__state' ) ).map( state => {
			const abbr = state.getAttribute('abbr');

			if ( this.data[ abbr ] ) {
				this.states[ abbr ] = new MapState( this, state, this.data[ abbr ] );
				state.setAttribute('status', 'active');
			} else {
				state.setAttribute('status', 'inactive');
			}

			if ( state.getAttribute('status') === 'active' ) {
				this.select.insertAdjacentHTML( 'beforeend', `<option value="${abbr}">${state.getAttribute('name')}</option>` );
			}
		} );

		document.addEventListener('keyup', (e) => {
			if ( this.active !== null && e.key === 'Escape' ) {
				this.deactivate();
			}
		} );

		const handleResize = () => {
			var width = this.map.parentNode.offsetWidth;
			var height = width * this.map.getAttribute('height') / this.map.getAttribute('width');
			this.map.style.width = width + 'px';
			this.map.style.height = height + 'px';

			if ( this.active ) {
				this.states[ this.active ].resetCoords();
			}
		}

		window.addEventListener('resize', handleResize);
		handleResize();
	}

	getMode() {
		if ( document.body.clientWidth < this.opts.mobileBreakpoint ) {
			return 'mobile';
		}

		return 'desktop';
	}

	activate( key ) {
		if ( this.active !== null ) {
			this.states[ this.active ].deactivate();
		}

		if ( this.getMode() === 'mobile' ) {
			document.body.style.overflow = 'hidden';
		}

		this.active = key;
		this.states[ this.active ].activate();
		this.select.value = this.states[ this.active ].key;
		addSvgClass( this.map, this.opts.activeClass );
	}

	deactivate() {
		if ( this.active !== null ) {
			this.states[ this.active ].deactivate();
		}

		this.active = null;
		this.select.value = '';
		document.body.style.overflow = '';
		removeSvgClass( this.map, this.opts.activeClass );
	}
}

export function statesMap( node, opts = {} ) {
	const dataNode = node.querySelector('script[type="application/json"]');
	const data = dataNode ? JSON.parse( dataNode.innerText ) : {};

	return new StatesMap( node, opts, data );
}

export const stateMaps = Array.from( document.querySelectorAll( '.jobs-by-state' ) ).map( statesMap );
